import {
  defaultEntitlement,
  toIsoString,
  userProfileSchema,
  type Entitlement,
  type UserProfile,
} from "@woven/contracts";
import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  GithubAuthProvider,
  GoogleAuthProvider,
  getAdditionalUserInfo,
  getIdToken as getIdTokenFirebase,
  onAuthStateChanged,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as signOutFirebase,
  type User,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createControlApiClient } from "../lib/api";
import type { WebEnv } from "../lib/env";
import { getSharedAppCheckToken } from "../lib/hooks";
import {
  AuthContext,
  type AccessSource,
  type AuthContextValue,
  type AuthSession,
  type ReauthenticationRequest,
  synthesizeProfile,
} from "./context";
import { getFirebaseClient } from "./firebase";
import { notifySignupBestEffort } from "./signup-notification";

const localAdminUser = {
  uid: "local-admin",
  email: "admin@local.woven.test",
  displayName: "Local Admin",
  photoURL: null,
} as User;
const localAdminEntitlement = defaultEntitlement(localAdminUser.uid, "admin");
const localAdminProfile = synthesizeProfile(localAdminUser, localAdminEntitlement.updatedAt);
const localAdminSession: AuthSession = { uid: localAdminUser.uid, generation: 0 };

export function AuthProvider({ env, children }: { env: WebEnv; children: ReactNode }) {
  const localAdminEnabled = import.meta.env.DEV && env.localAdminBypass;
  const [user, setUser] = useState<User | null>(() => (localAdminEnabled ? localAdminUser : null));
  const [loading, setLoading] = useState(() => !localAdminEnabled);
  const [profile, setProfile] = useState<UserProfile | null>(() =>
    localAdminEnabled ? localAdminProfile : null
  );
  const [entitlement, setEntitlement] = useState<Entitlement | null>(() =>
    localAdminEnabled ? localAdminEntitlement : null
  );
  const [accessSource, setAccessSource] = useState<AccessSource>(() =>
    localAdminEnabled ? "local" : "default"
  );
  const [accessRequested, setAccessRequested] = useState<boolean | null>(() =>
    localAdminEnabled ? false : null
  );
  const [error, setError] = useState<Error | null>(null);
  const membershipGeneration = useRef(0);
  const currentUid = useRef(user?.uid ?? null);
  const active = useRef(false);
  const sessionGeneration = useRef(0);
  const [session, setSession] = useState<AuthSession | null>(
    localAdminEnabled ? localAdminSession : null
  );
  const currentSession = useRef(session);
  const appliedUser = useRef(user);

  const observeSession = useCallback((authUser: User | null) => {
    const uid = authUser?.uid ?? null;
    if (currentUid.current === uid) return currentSession.current;
    const generation = ++sessionGeneration.current;
    const next = uid ? { uid, generation } : null;
    currentUid.current = uid;
    currentSession.current = next;
    setSession(next);
    return next;
  }, []);

  const isCurrentSession = useCallback(
    (expected: AuthSession) =>
      active.current &&
      currentSession.current === expected &&
      currentUid.current === expected.uid &&
      (localAdminEnabled || getFirebaseClient(env).auth.currentUser?.uid === expected.uid),
    [env, localAdminEnabled]
  );

  const loadMembership = useCallback(
    async (
      authUser: User,
      expected: AuthSession,
      options: { background?: boolean; signal?: AbortSignal } = {}
    ) => {
      options.signal?.throwIfAborted();
      const generation = ++membershipGeneration.current;
      if (!options.background) {
        setLoading(true);
        setError(null);
      }
      try {
        const { db } = getFirebaseClient(env);
        const client = createControlApiClient(
          env,
          async () => {
            if (!isCurrentSession(expected)) throw new Error("The signed-in account changed.");
            const token = await getIdTokenFirebase(authUser);
            if (!isCurrentSession(expected)) throw new Error("The signed-in account changed.");
            return token;
          },
          () => getSharedAppCheckToken(env),
          () => {
            if (!isCurrentSession(expected)) throw new Error("The signed-in account changed.");
          }
        );
        const [userSnap, membership] = await Promise.all([
          getDoc(doc(db, "users", authUser.uid)),
          client.getMe(options.signal),
        ]);
        if (membership.user.uid !== authUser.uid || membership.entitlement.uid !== authUser.uid) {
          throw new Error("Membership does not match the signed-in account.");
        }

        options.signal?.throwIfAborted();
        if (generation !== membershipGeneration.current || !isCurrentSession(expected)) return;

        let liveProfile: UserProfile | null = null;
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const parsed = userProfileSchema.safeParse({
            ...userData,
            createdAt: toIsoString(userData.createdAt),
            updatedAt: toIsoString(userData.updatedAt),
          });
          liveProfile = parsed.success ? parsed.data : null;
        }

        setProfile(liveProfile ?? membership.user);
        setEntitlement(membership.entitlement);
        setAccessRequested(membership.accessRequested);
        setAccessSource("firestore");
        setError(null);
      } catch (cause) {
        if (generation !== membershipGeneration.current || !isCurrentSession(expected)) return;
        if (options.signal?.aborted) throw cause;
        setEntitlement(defaultEntitlement(authUser.uid));
        setAccessRequested(null);
        setProfile(synthesizeProfile(authUser, new Date().toISOString()));
        setAccessSource("default");
        setError(new Error("Unable to load your profile and entitlements."));
        throw cause;
      } finally {
        if (generation === membershipGeneration.current && isCurrentSession(expected))
          setLoading(false);
      }
    },
    [env, isCurrentSession]
  );

  const applyAuthUser = useCallback(
    (authUser: User | null) => {
      if (authUser && appliedUser.current === authUser && currentUid.current === authUser.uid) {
        return currentSession.current;
      }
      const next = observeSession(authUser);
      appliedUser.current = authUser;
      membershipGeneration.current += 1;
      setProfile(null);
      setEntitlement(null);
      setAccessRequested(null);
      setAccessSource("default");
      setError(null);
      setUser(authUser);
      setLoading(Boolean(authUser));
      if (authUser) void loadMembership(authUser, next!).catch(() => {});
      return next;
    },
    [loadMembership, observeSession]
  );

  const authenticationSession = useCallback(
    (authUser: User, startedAt: number): AuthSession => {
      if (
        !active.current ||
        getFirebaseClient(env).auth.currentUser?.uid !== authUser.uid ||
        (sessionGeneration.current !== startedAt &&
          !(sessionGeneration.current === startedAt + 1 && currentUid.current === authUser.uid))
      ) {
        throw new Error("The signed-in account changed. Authentication was cancelled.");
      }
      return applyAuthUser(authUser)!;
    },
    [env, applyAuthUser]
  );

  const refreshMembership = useCallback(
    async (options?: { background?: boolean; signal?: AbortSignal }) => {
      if (localAdminEnabled) return;
      if (!user || !session || !isCurrentSession(session)) {
        throw new Error("The signed-in account changed. Membership refresh was cancelled.");
      }
      await loadMembership(user, session, options);
    },
    [isCurrentSession, loadMembership, localAdminEnabled, session, user]
  );

  useEffect(() => {
    active.current = true;
    let subscribed = true;
    if (localAdminEnabled) {
      return () => {
        active.current = false;
      };
    }

    const { auth } = getFirebaseClient(env);
    const unsubscribe = onAuthStateChanged(
      auth,
      (authUser) => {
        if (!subscribed) return;
        applyAuthUser(authUser);
      },
      (authError) => {
        if (!subscribed) return;
        membershipGeneration.current += 1;
        observeSession(null);
        appliedUser.current = null;
        setUser(null);
        setProfile(null);
        setEntitlement(null);
        setAccessRequested(null);
        setAccessSource("default");
        setError(authError);
        setLoading(false);
      }
    );
    return () => {
      subscribed = false;
      active.current = false;
      membershipGeneration.current += 1;
      sessionGeneration.current += 1;
      currentUid.current = null;
      currentSession.current = null;
      appliedUser.current = null;
      unsubscribe();
    };
  }, [env, localAdminEnabled, applyAuthUser, observeSession]);

  const signInWithGoogle = useCallback(async () => {
    const { auth } = getFirebaseClient(env);
    const startedAt = sessionGeneration.current;
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    if (getAdditionalUserInfo(result)?.isNewUser === true) {
      void notifySignupBestEffort(env, result.user);
    }
    return authenticationSession(result.user, startedAt);
  }, [env, authenticationSession]);

  const signInWithGithub = useCallback(async () => {
    const { auth } = getFirebaseClient(env);
    const startedAt = sessionGeneration.current;
    const provider = new GithubAuthProvider();
    const result = await signInWithPopup(auth, provider);
    if (getAdditionalUserInfo(result)?.isNewUser === true) {
      void notifySignupBestEffort(env, result.user);
    }
    return authenticationSession(result.user, startedAt);
  }, [env, authenticationSession]);

  const signInWithEmail = useCallback(
    async (email: string, password: string) => {
      const { auth } = getFirebaseClient(env);
      const startedAt = sessionGeneration.current;
      const result = await signInWithEmailAndPassword(auth, email, password);
      return authenticationSession(result.user, startedAt);
    },
    [env, authenticationSession]
  );

  const createAccountWithEmail = useCallback(
    async (email: string, password: string) => {
      const { auth } = getFirebaseClient(env);
      const startedAt = sessionGeneration.current;
      const result = await createUserWithEmailAndPassword(auth, email, password);
      void notifySignupBestEffort(env, result.user);
      return authenticationSession(result.user, startedAt);
    },
    [env, authenticationSession]
  );

  const reauthenticate = useCallback(
    async (request: ReauthenticationRequest) => {
      if (localAdminEnabled) {
        throw new Error("Reauthentication is unavailable for the local admin bypass.");
      }
      const { auth } = getFirebaseClient(env);
      const authUser = user;
      const expectedUid = authUser?.uid;
      if (
        !authUser ||
        !expectedUid ||
        !session ||
        !isCurrentSession(session) ||
        currentUid.current !== expectedUid ||
        auth.currentUser?.uid !== expectedUid
      ) {
        throw new Error("The signed-in account changed. Reauthentication was cancelled.");
      }
      if (!authUser.providerData.some(({ providerId }) => providerId === request.providerId)) {
        throw new Error("That sign-in method is not linked to the current account.");
      }

      const result =
        request.providerId === "password"
          ? authUser.email
            ? await reauthenticateWithCredential(
                authUser,
                EmailAuthProvider.credential(authUser.email, request.password)
              )
            : null
          : await reauthenticateWithPopup(
              authUser,
              request.providerId === "google.com"
                ? new GoogleAuthProvider()
                : new GithubAuthProvider()
            );
      if (!result) {
        throw new Error("The current account does not have an email address.");
      }
      if (
        result.user.uid !== expectedUid ||
        !isCurrentSession(session) ||
        user?.uid !== expectedUid ||
        currentUid.current !== expectedUid ||
        auth.currentUser?.uid !== expectedUid
      ) {
        throw new Error("The signed-in account changed. Reauthentication was cancelled.");
      }

      await getIdTokenFirebase(result.user, true);
      if (
        !isCurrentSession(session) ||
        user?.uid !== expectedUid ||
        currentUid.current !== expectedUid ||
        auth.currentUser?.uid !== expectedUid
      ) {
        throw new Error("The signed-in account changed. Reauthentication was cancelled.");
      }
    },
    [env, isCurrentSession, localAdminEnabled, session, user]
  );

  const signOut = useCallback(async () => {
    if (localAdminEnabled) {
      return;
    }
    const { auth } = getFirebaseClient(env);
    if (
      !active.current ||
      (user && (!session || !isCurrentSession(session))) ||
      currentUid.current !== (user?.uid ?? null) ||
      (auth.currentUser && auth.currentUser.uid !== user?.uid)
    ) {
      throw new Error("The signed-in account changed. Sign out from the current account instead.");
    }
    const expected = currentSession.current;
    await signOutFirebase(auth);
    if (
      !active.current ||
      currentSession.current !== expected ||
      (auth.currentUser && auth.currentUser.uid !== user?.uid)
    )
      return;
    observeSession(null);
    appliedUser.current = null;
    membershipGeneration.current += 1;
    setUser(null);
    setProfile(null);
    setEntitlement(null);
    setAccessRequested(null);
    setAccessSource("default");
    setError(null);
    setLoading(false);
  }, [env, isCurrentSession, localAdminEnabled, observeSession, session, user]);

  const getFirebaseIdToken = useCallback(
    async (forceRefresh = false) => {
      if (!user) return null;
      const assertCurrent = () => {
        if (!session || !isCurrentSession(session)) {
          throw new Error("The signed-in account changed. Token retrieval was cancelled.");
        }
      };
      assertCurrent();
      let token: string | null;
      try {
        token = await getIdTokenFirebase(user, forceRefresh);
      } catch {
        token = null;
      }
      assertCurrent();
      return token;
    },
    [isCurrentSession, session, user]
  );

  const value = useMemo<AuthContextValue>(() => {
    const access = entitlement?.accessLevel ?? "guest";
    return {
      user,
      session,
      isCurrentSession,
      refreshMembership,
      accessRequested,
      loading,
      profile,
      entitlement,
      access,
      accessSource,
      usingMockAccess: accessSource === "mock" || accessSource === "local",
      error,
      signInWithGoogle,
      signInWithGithub,
      signInWithEmail,
      createAccountWithEmail,
      reauthenticate,
      signOut,
      getIdToken: getFirebaseIdToken,
    };
  }, [
    user,
    session,
    isCurrentSession,
    refreshMembership,
    accessRequested,
    loading,
    profile,
    entitlement,
    accessSource,
    error,
    signInWithGoogle,
    signInWithGithub,
    signInWithEmail,
    createAccountWithEmail,
    reauthenticate,
    signOut,
    getFirebaseIdToken,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
